import { Link } from "react-router-dom";
import { useCart } from "@/App";
import { Button } from "@/components/ui/button";
import { GitCompare, X, ShoppingCart, Check, Minus } from "lucide-react";

export default function Comparador() {
  const { compareList, removeFromCompare, clearCompare, addToCart } = useCart();

  const formatPrice = (price) => {
    return new Intl.NumberFormat("es-UY", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  // Get all unique spec keys from all products
  const allSpecKeys = [...new Set(
    compareList.flatMap(product => Object.keys(product.specs || {}))
  )];

  if (compareList.length === 0) {
    return (
      <div className="min-h-screen bg-background" data-testid="comparador-empty">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <GitCompare className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="font-heading text-2xl md:text-3xl mb-2">COMPARADOR VACÍO</h1>
          <p className="text-muted-foreground mb-6">
            Agregá productos para comparar sus características
          </p>
          <Link to="/tienda">
            <Button className="bg-primary hover:bg-primary/90">
              Ver Productos
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="comparador-page">
      {/* Header */}
      <div className="bg-primary text-white py-8 relative">
        <div className="relative industrial-texture"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl">COMPARADOR</h1>
            <p className="text-white/80 mt-2">
              Comparando {compareList.length} producto{compareList.length !== 1 ? "s" : ""}
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={clearCompare}
            className="text-white hover:bg-white/10"
          >
            Limpiar todo
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Comparison table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr>
                <th className="text-left p-4 bg-muted font-heading uppercase tracking-wide w-40">
                  Producto
                </th>
                {compareList.map((product) => (
                  <th key={product.product_id} className="p-4 bg-muted">
                    <div className="relative">
                      <button
                        onClick={() => removeFromCompare(product.product_id)}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center hover:bg-destructive/80"
                        aria-label="Quitar de comparación"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <Link to={`/producto/${product.product_id}`}>
                        <div className="aspect-square w-32 mx-auto bg-white rounded-sm overflow-hidden mb-3">
                          {product.images?.[0] ? (
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="w-full h-full object-contain p-2"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                              Sin imagen
                            </div>
                          )}
                        </div>
                        <h3 className="font-heading text-sm hover:text-primary line-clamp-2 text-center">
                          {product.name}
                        </h3>
                      </Link>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Price row */}
              <tr>
                <td className="p-4 bg-muted/50 font-medium">Precio</td>
                {compareList.map((product) => (
                  <td key={product.product_id} className="p-4 text-center">
                    <span className="font-heading text-2xl text-accent">
                      {formatPrice(product.price)}
                    </span>
                  </td>
                ))}
              </tr>

              {/* Brand row */}
              <tr>
                <td className="p-4 bg-muted/50 font-medium">Marca</td>
                {compareList.map((product) => (
                  <td key={product.product_id} className="p-4 text-center">
                    {product.brand || <Minus className="w-4 h-4 mx-auto text-muted-foreground" />}
                  </td>
                ))}
              </tr>

              {/* Stock row */}
              <tr>
                <td className="p-4 bg-muted/50 font-medium">Disponibilidad</td>
                {compareList.map((product) => (
                  <td key={product.product_id} className="p-4 text-center">
                    {product.stock > 0 ? (
                      <span className="text-green-600 flex items-center justify-center gap-1">
                        <Check className="w-4 h-4" />
                        En stock ({product.stock})
                      </span>
                    ) : (
                      <span className="text-destructive">Sin stock</span>
                    )}
                  </td>
                ))}
              </tr>

              {/* Spec rows */}
              {allSpecKeys.map((specKey) => (
                <tr key={specKey}>
                  <td className="p-4 bg-muted/50 font-medium capitalize">{specKey}</td>
                  {compareList.map((product) => (
                    <td key={product.product_id} className="p-4 text-center">
                      {product.specs?.[specKey] || (
                        <Minus className="w-4 h-4 mx-auto text-muted-foreground" />
                      )}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Add to cart row */}
              <tr>
                <td className="p-4 bg-muted/50 font-medium"></td>
                {compareList.map((product) => (
                  <td key={product.product_id} className="p-4 text-center">
                    <Button
                      onClick={() => addToCart(product.product_id)}
                      disabled={product.stock <= 0}
                      className="bg-accent hover:bg-accent/90 text-white font-bold uppercase tracking-wide"
                      data-testid={`compare-add-cart-${product.product_id}`}
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Agregar
                    </Button>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Add more products */}
        {compareList.length < 4 && (
          <div className="mt-8 text-center">
            <p className="text-muted-foreground mb-4">
              Podés agregar hasta {4 - compareList.length} producto{4 - compareList.length !== 1 ? "s" : ""} más
            </p>
            <Link to="/tienda">
              <Button variant="outline">Agregar más productos</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
